import { Briefcase } from '@lucide/vue'

import { AssetWizardFlow } from '@/store/ai/assetWizard/utils/types'

import {
  amountField,
  assetNameField,
  currencyField,
  dateField,
  descriptionField,
} from '../utils/fields'
import {
  SUMMARY_STEP,
  TAGS_STEP,
  valueStep,
} from '../utils/steps'
import { CASH_FLOWS_STEP_KEY, ENTITIES_STEP_KEY } from '../utils/const'
import { WizardDefinition } from '../utils/types'
import { TagRef } from '@types'

import { privateFundSchema } from './schema'
import { create } from './create'
import CashFlowsStep from '../components/CashFlowsStep.vue'
import EntitiesStep from '../components/EntitiesStep.vue'

/** Same string the legacy Private fund wizard files commitments under. */
const FUND_TYPE = 'private fund'

const DEFAULT_TAGS: TagRef[] = [
  { tag_name: 'Asset Class', tag_value: 'Alternative Investments' },
  { tag_name: 'Investment Strategy', tag_value: 'Private Equity' },
]

/**
 * Private equity, private credit, venture and the rest — the same steps as
 * PrivateFundSubscribeAndCommit, minus the split: an entity here is not
 * sized by a share of one commitment, because each becomes its own full
 * `add position` record for the full amount every other one also gets. See
 * AssetWizardFlow.PRIVATE_FUND_SUBSCRIBE_AND_COMMIT for the sibling that
 * splits a commitment across entities instead.
 */
export const privateFundWizard: WizardDefinition = {
  flow: AssetWizardFlow.PRIVATE_FUND,
  label: 'Private fund',
  icon: Briefcase,
  schema: privateFundSchema,
  steps: [
    {
      key: 'fund',
      label: 'Fund',
      heading: 'What is this?',
      description:
        'Private equity, private credit, venture and hedge funds all go here.',
      fields: [
        assetNameField('Fund name', 'Name this fund'),
        descriptionField(),
      ],
    },
    {
      key: 'commitment',
      label: 'Commitment',
      heading: 'How much did you commit?',
      description:
        'The amount pledged to this fund — not what has been called yet. Calls and distributions have their own step.',
      fields: [
        amountField('Committed amount'),
        dateField('Date of investment'),
        currencyField(),
      ],
    },
    {
      key: ENTITIES_STEP_KEY,
      label: 'Investing entities',
      heading: 'Who is investing?',
      description: 'Each investor becomes its own asset and transaction bundle in the fund, for the full amount.',
      component: EntitiesStep,
    },
    {
      key: CASH_FLOWS_STEP_KEY,
      label: 'Cash flows',
      heading: 'Any capital calls or distributions yet?',
      badge: 'Optional',
      description: 'Capital calls, fees and distributions to date. Add them now or log them as they arrive.',
      component: CashFlowsStep,
    },
    /*
     * The shared valuation step, in a fund's words: what a fund is worth is a
     * NAV struck on a date, which is the same mark every other flow records.
     */
    valueStep({
      heading: 'What is the latest NAV?',
      description:
        'Record the NAV and the date it was struck. Each mark dates the value it sets.',
    }),
    TAGS_STEP,
    SUMMARY_STEP,
  ],
  records: [
    {
      key: 'fund',
      label: 'Fund',
      icon: Briefcase,
      tone: 'brand',
      transactionType: 'add position',
      heading: 'assetName',
      fields: ['amount', 'date', 'currency', 'description'],
    },
  ],
  constants: { assetType: FUND_TYPE },
  defaultTags: DEFAULT_TAGS,
  create,
}
